import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Button } from "./ui/button";
import { CheckCircle, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Dashboard banner that surfaces invoices a client has self-marked as paid.
 * Admins can one-click verify (→ status=paid) or reject the claim from here.
 * Auto-hides when there are zero pending claims.
 */
export default function PendingPaymentVerifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/invoices?pending_verification=true&limit=20");
      setItems(data.invoices || []);
    } catch { /* silent — non-critical widget */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleVerify = async (inv) => {
    setActingId(inv.id);
    try {
      await API.post(`/invoices/${inv.id}/verify-paid`);
      toast.success(`Verified — ${inv.reference} marked paid`);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    } finally { setActingId(null); }
  };

  const handleRejectSubmit = async () => {
    if (!rejectFor) return;
    setActingId(rejectFor.id);
    try {
      await API.post(`/invoices/${rejectFor.id}/reject-paid-claim`, { reason: rejectReason });
      toast.success("Claim rejected — invoice returned to outstanding");
      setRejectFor(null); setRejectReason("");
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally { setActingId(null); }
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="mb-6 border border-amber-300 dark:border-amber-700/60 bg-amber-50/80 dark:bg-amber-900/20 rounded-lg overflow-hidden" data-testid="pending-paid-banner">
      <div className="flex items-center justify-between px-4 py-3 border-b border-amber-200 dark:border-amber-800/50">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center">
            <CheckCircle size={16} weight="fill" className="text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-semibold font-[Chivo] text-amber-900 dark:text-amber-200">
              {items.length} invoice{items.length !== 1 ? "s" : ""} marked paid by client — awaiting your verification
            </p>
            <p className="text-[11px] text-amber-700 dark:text-amber-300/80">Confirm funds have arrived, then verify in one click.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate("/invoices?pending_verification=1")} data-testid="pending-paid-view-all">
          View all <ArrowRight size={12} className="ml-1" />
        </Button>
      </div>
      <div className="divide-y divide-amber-200/70 dark:divide-amber-800/40">
        {items.slice(0, 5).map(inv => {
          const total = inv.total_inc_vat ?? inv.grand_total ?? inv.total ?? 0;
          return (
            <div key={inv.id} className="px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`pending-paid-${inv.id}`}>
              <button onClick={() => navigate(`/invoices/${inv.id}`)} className="flex-1 min-w-0 text-left hover:underline">
                <p className="text-sm font-medium truncate">{inv.title || inv.reference}</p>
                <p className="text-[11px] text-muted-foreground">
                  <span className="font-mono">{inv.reference}</span>
                  {inv.client_name && <> &middot; {inv.client_name}</>}
                  {inv.client_marked_paid_method && <> &middot; via {inv.client_marked_paid_method}</>}
                  {inv.client_marked_paid_reference && <> &middot; ref <span className="font-mono">{inv.client_marked_paid_reference}</span></>}
                </p>
              </button>
              <p className="text-sm font-bold font-[Chivo] tabular-nums whitespace-nowrap">£{total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => handleVerify(inv)}
                  disabled={actingId === inv.id}
                  data-testid={`pending-paid-verify-${inv.id}`}
                >
                  <CheckCircle size={13} weight="fill" className="mr-1" /> Verify
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] text-rose-600 hover:text-rose-700 border-rose-200 dark:border-rose-900/40"
                  onClick={() => { setRejectFor(inv); setRejectReason(""); }}
                  disabled={actingId === inv.id}
                  data-testid={`pending-paid-reject-${inv.id}`}
                >
                  <X size={13} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          );
        })}
        {items.length > 5 && (
          <button onClick={() => navigate("/invoices?pending_verification=1")} className="block w-full px-4 py-2 text-center text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-100/50 dark:hover:bg-amber-900/30">
            +{items.length - 5} more — view all
          </button>
        )}
      </div>

      {rejectFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => actingId === null && setRejectFor(null)}>
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="pending-paid-reject-dialog">
            <div>
              <h3 className="font-semibold font-[Chivo] text-base">Reject paid claim</h3>
              <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{rejectFor.reference} &middot; {rejectFor.client_name}</p>
            </div>
            <p className="text-xs text-muted-foreground">The invoice will return to outstanding. The reason is recorded in the timeline. Optional but recommended.</p>
            <div>
              <label className="text-xs font-medium">Reason (optional)</label>
              <textarea
                rows={3}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="e.g. Funds not received in our account."
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm"
                data-testid="pending-paid-reject-reason"
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectFor(null)} disabled={actingId === rejectFor.id} data-testid="pending-paid-reject-cancel">Cancel</Button>
              <Button onClick={handleRejectSubmit} disabled={actingId === rejectFor.id} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="pending-paid-reject-submit">
                {actingId === rejectFor.id ? "Rejecting…" : "Reject claim"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
